<template>
    <div class="container-fluid">
        <div class="box">
            <div class="row mb-3">
                <div class="col-12 col-md-6">
                    <label class="form-label">{{ $t('import.importer') }} (.txt)</label>
                    <div class="d-flex align-items-center" style="gap: 8px;">
                        <b-form-file
                            v-model="file"
                            accept=".txt"
                            :placeholder="$t('global.choisir_fichier')"
                            :browse-text="$t('global.parcourir')"
                            @change="onFileChange"
                            :disabled="analysing || creating"
                        />
                        <b-button
                            v-if="rows.length"
                            variant="primary"
                            class="flex-shrink-0"
                            :disabled="analysing || creating"
                            @click="analyseRows"
                        >
                            <font-awesome-icon v-if="analysing" :icon="['fas', 'spinner']" pulse />
                            {{ $t('global.refresh') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <div v-if="acte_type_error" class="alert alert-danger">
                {{ $t('import_performant_trot.acte_type_error') }}
            </div>

            <div v-if="format_error" class="alert alert-warning">
                <strong>{{ $t('import_performant_trot.format_error_title') }}</strong>
                {{ $t('import_performant_trot.format_error_message') }}
                <span v-if="format_error_lines.length">
                    {{ $t('import_performant_trot.format_error_lines') }} {{ format_error_lines.join(', ') }}.
                </span>
            </div>

            <div v-if="analysing" class="text-center py-3">
                <font-awesome-icon :icon="['fas', 'spinner']" pulse /> {{ $t('import_performant_trot.analysing') }}
            </div>

            <div v-if="!analysing && rows.length">
                <div class="d-flex flex-wrap mb-3" style="gap: 8px;">
                    <b-badge variant="success"   class="px-3 py-2">{{ countByStatus('ready') }} {{ $t(countByStatus('ready') > 1 ? 'import_performant_trot.badge_prets' : 'import_performant_trot.badge_pret') }}</b-badge>
                    <b-badge variant="warning"   class="px-3 py-2">{{ countByStatus('duplicate') }} {{ $t(countByStatus('duplicate') > 1 ? 'import_performant_trot.badge_doublons' : 'import_performant_trot.badge_doublon') }}</b-badge>
                    <b-badge variant="danger"    class="px-3 py-2">{{ countByStatus('not_found') }} {{ $t(countByStatus('not_found') > 1 ? 'import_performant_trot.badge_introuvables' : 'import_performant_trot.badge_introuvable') }}</b-badge>
                    <b-badge variant="info"      class="px-3 py-2" v-if="countByStatus('created')">{{ countByStatus('created') }} {{ $t(countByStatus('created') > 1 ? 'import_performant_trot.badge_crees' : 'import_performant_trot.badge_cree') }}</b-badge>
                    <b-badge variant="secondary" class="px-3 py-2" v-if="countByStatus('error')">{{ countByStatus('error') }} {{ $t(countByStatus('error') > 1 ? 'import_performant_trot.badge_erreurs' : 'import_performant_trot.badge_erreur') }}</b-badge>
                </div>

                <p v-if="countByStatus('duplicate') > 0 || countByStatus('not_found') > 0" class="text-muted small mb-3">
                    <span v-if="countByStatus('duplicate') > 0">{{ $t(countByStatus('duplicate') > 1 ? 'import_performant_trot.warning_duplicate_plural' : 'import_performant_trot.warning_duplicate_singular') }}</span>
                    <span v-if="countByStatus('not_found') > 0"> {{ $t(countByStatus('not_found') > 1 ? 'import_performant_trot.warning_not_found_plural' : 'import_performant_trot.warning_not_found_singular') }}</span>
                </p>

                <div class="mb-3" v-if="countByStatus('ready') > 0">
                    <b-button variant="primary" :disabled="creating" @click="createActes">
                        <font-awesome-icon v-if="creating" :icon="['fas', 'spinner']" pulse />
                        {{ $t(countByStatus('ready') > 1 ? 'import_performant_trot.btn_creer_plural' : 'import_performant_trot.btn_creer_singular', { count: countByStatus('ready') }) }}
                    </b-button>
                </div>

                <div class="row align-items-center mb-1">
                    <div class="col-12 col-md-auto mr-auto text-sm-left text-center">
                        {{ rows.length === 1 ? `1 ${$t('table.general.resultat')}` : `${rows.length} ${$t('table.general.resultats')}` }}
                    </div>
                    <div class="col-12 col-md-auto text-sm-right text-center" v-if="rows.length > perPage">
                        <b-pagination v-model="currentPage" :total-rows="rows.length" :per-page="perPage" aria-controls="my-table" />
                    </div>
                </div>

                <div class="table-responsive mx-3">
                    <b-table
                        :items="rows"
                        :fields="columns"
                        :per-page="perPage"
                        :current-page="currentPage"
                        small striped hover bordered show-empty
                    >
                        <template #cell(status)="{ item }">
                            <b-badge :variant="statusVariant(item.status)">{{ statusLabel(item.status) }}</b-badge>
                        </template>
                    </b-table>
                </div>

                <div class="row align-items-center mt-1" v-if="rows.length > perPage">
                    <div class="col-12 col-md-auto text-sm-right text-center ml-md-auto">
                        <b-pagination v-model="currentPage" :total-rows="rows.length" :per-page="perPage" aria-controls="my-table" />
                    </div>
                </div>
            </div>

            <div v-else-if="!analysing && parsed" class="alert alert-warning">
                {{ $t('import_performant_trot.no_data') }}
            </div>
        </div>
    </div>
</template>

<script>
    import ActeMixin from '@/mixins/Actes.js'
    import ActeTypeMixin from '@/mixins/ActeType.js'
    import HorseMixin from '@/mixins/Horse.js'
    import LieuMixin from '@/mixins/Lieux.js'

    export default {
        name: 'ImportPerformantTrot',
        mixins: [ActeMixin, ActeTypeMixin, HorseMixin, LieuMixin],

        data() {
            return {
                raw_field_keys: [
                    'nom_cheval', 'resultat', 'driver', 'distance', 'temps', 'allure', 'gains',
                    'entraineur', 'ecurie_proprietaire', 'adresse', 'ville', 'cp',
                    'date_raw', 'hippodrome', 'nom_prix', 'allocation', 'discipline',
                    'proprietaire_1', 'proprietaire_2', 'proprietaire_3', 'proprietaire_4', '_flag',
                ],
                discipline_map:     { 'A': 'Attelé', 'M': 'Monté' },
                file:               null,
                rows:               [],
                parsed:             false,
                analysing:          false,
                creating:           false,
                format_error:       false,
                format_error_lines: [],
                acte_type_error:    false,
                acte_type:          null,
                questions_map:      {},
                lieu_type_id:       null,
                currentPage:        1,
                perPage:            20,
            }
        },

        async mounted() {
            await this.loadActeType()
            await this.loadLieuType()
        },

        methods: {
            parseDate(raw) {
                if (!raw) return null
                const s = raw.trim()
                if (!s) return null
                const day   = s.length === 7 ? s.slice(0, 1).padStart(2, '0') : s.slice(0, 2)
                const month = s.length === 7 ? s.slice(1, 3) : s.slice(2, 4)
                const year  = s.length === 7 ? s.slice(3) : s.slice(4)
                return `${year}-${month}-${day}`
            },

            parseTemps(raw) {
                if (!raw) return null
                const s = raw.trim()
                if (!s) return null
                const match = s.match(/^(\d+)'(\d+)"(\d+)$/)
                if (!match) return { brut: s }
                return {
                    minutes:       parseInt(match[1], 10),
                    secondes:      parseInt(match[2], 10),
                    centisecondes: parseInt(match[3], 10),
                }
            },

            formatTemps(t) {
                if (!t) return null
                if (t.brut) return t.brut
                return `${t.minutes}'${String(t.secondes).padStart(2, '0')}"${String(t.centisecondes).padStart(2, '0')}`
            },

            parseLine(line) {
                const parts = line.split('|').map(p => p.trim())
                const raw = {}
                this.raw_field_keys.forEach((key, i) => { raw[key] = parts[i] || null })
                const temps = this.parseTemps(raw.temps)
                return {
                    actes_date:  this.parseDate(raw.date_raw),
                    actes_horse: raw.nom_cheval,
                    specialite:  this.discipline_map[raw.discipline] || raw.discipline,
                    hippodrome:  raw.hippodrome,
                    nom_prix:    raw.nom_prix,
                    distance:    raw.distance ? (parseInt(raw.distance, 10) || raw.distance) : null,
                    driver:      raw.driver,
                    rang:        raw.resultat,
                    gain:        raw.gains ? (parseInt(raw.gains, 10) || raw.gains) : null,
                    temps:       temps,
                    temps_str:   this.formatTemps(temps),
                    status:      'pending',
                    horse_id:    null,
                    lieu_id:     null,
                }
            },

            async loadActeType() {
                const at = await this.getActeTypeByCode('PERF_COURSE_TROT')
                if (!at) {
                    this.acte_type_error = true
                    return
                }
                this.acte_type = at
                const questions = await this.getQuestionsByActeType(at.actestype_id)
                this.questions_map = {}
                questions.forEach(q => { this.questions_map[q.question_code] = q.question_id })
            },

            async loadLieuType() {
                const types = await this.getTypeLieu()
                const other = types.find(t => t.lieutype_code !== 'RESI')
                if (other) this.lieu_type_id = other.lieutype_id
            },


            onFileChange(event) {
                const f = event.target.files[0]
                if (!f) return
                const reader = new FileReader()
                reader.onload = async (e) => {
                    this.format_error = false
                    this.format_error_lines = []
                    this.rows = []
                    this.parsed = false
                    const lines = e.target.result.split('\n').filter(l => l.trim())
                    if (!this.validateFormat(lines)) return
                    this.rows = lines.map(this.parseLine, this)
                    this.parsed = true
                    this.currentPage = 1
                    await this.analyseRows()
                }
                reader.readAsText(f, 'utf-8')
            },

            validateFormat(lines) {
                const invalidLines = []
                lines.forEach((line, i) => {
                    const parts = line.split('|').map(p => p.trim())
                    const hasEnoughCols = parts.length >= 17
                    const hasValidDate = /^\d{7,8}$/.test(parts[12] || '')
                    if (!hasEnoughCols || !hasValidDate) invalidLines.push(i + 1)
                })
                if (invalidLines.length) {
                    this.format_error = true
                    this.format_error_lines = invalidLines
                    return false
                }
                return true
            },

            async analyseRows() {
                if (!this.acte_type) return
                this.analysing = true
                for (const row of this.rows) {
                    if (!row.actes_horse || !row.actes_date) {
                        this.$set(row, 'status', 'not_found')
                        continue
                    }
                    const horses = await this.getHorseByNom(row.actes_horse)
                    if (horses.length === 0) {
                        this.$set(row, 'status', 'not_found')
                        continue
                    }
                    const horse = horses[0]
                    this.$set(row, 'horse_id', horse.horse_id)
                    this.$set(row, 'lieu_id', await this.findOrCreateLieuByLabel(row.hippodrome, this.lieu_type_id))
                    const existing = await this.getActeByHorseTypeDate(horse.horse_id, this.acte_type.actestype_id, row.actes_date)
                    this.$set(row, 'status', existing ? 'duplicate' : 'ready')
                }
                this.analysing = false
            },

            async createActes() {
                if (!this.acte_type) return
                this.creating = true
                for (const row of this.rows) {
                    if (row.status !== 'ready') continue
                    await this.addActes([row.horse_id], 1, new Date(row.actes_date), '', this.acte_type.actestype_id, 0, null, [], this.buildReponses(row), null, null)
                    this.$set(row, 'status', 'created')
                }
                this.creating = false
            },

            pushReponse(reponses, codes, value, hidden = null) {
                if (value === null || value === undefined || value === '') return
                const code = codes.find(c => this.questions_map[c])
                if (!code) return
                reponses.push({
                    reponse_question:    this.questions_map[code],
                    reponse_data:        [String(value)],
                    reponse_data_hidden: hidden,
                })
            },

            buildReponses(row) {
                const reponses = []
                this.pushReponse(reponses, ['Spécialité', 'Discipline'], row.specialite)
                this.pushReponse(reponses, ['Hippodrome'],
                    row.hippodrome ? `HIPPODROME ${row.hippodrome.toUpperCase()}` : null,
                    row.lieu_id ? { type: 'lieu', id: row.lieu_id } : null)
                this.pushReponse(reponses, ['Nom de la course'], row.nom_prix)
                this.pushReponse(reponses, ['Distance de la course (m)', 'Distance de la course'], row.distance)
                this.pushReponse(reponses, ['Driver / Jockey', 'Jockey', 'Driver'], row.driver)
                this.pushReponse(reponses, ['RESULTAT : rang', 'RESULTAT : Rang'], row.rang)
                this.pushReponse(reponses, ['RESULTAT : gain', 'RESULTAT : Gain'], row.gain)
                this.pushReponse(reponses, ['RESULTAT : Temps', 'Résultat : Temps'], row.temps_str)
                return reponses
            },

            countByStatus(status) {
                return this.rows.filter(r => r.status === status).length
            },

            statusVariant(status) {
                switch (status) {
                    case 'ready':     return 'success'
                    case 'duplicate': return 'warning'
                    case 'not_found': return 'danger'
                    case 'created':   return 'info'
                    case 'error':     return 'dark'
                    default:          return 'secondary'
                }
            },

            statusLabel(status) {
                const map = {
                    pending:   'import_performant_trot.status_pending',
                    ready:     'import_performant_trot.status_ready',
                    duplicate: 'import_performant_trot.status_duplicate',
                    not_found: 'import_performant_trot.status_not_found',
                    created:   'import_performant_trot.status_created',
                    error:     'import_performant_trot.status_error',
                }
                return map[status] ? this.$t(map[status]) : status
            },
        },
        
        computed: {
            columns() {
                return [
                    { key: 'status',      label: this.$t('import_performant_trot.col_statut'),    thStyle: { width: '130px' } },
                    { key: 'actes_date',  label: this.$t('import_performant_trot.col_date') },
                    { key: 'actes_horse', label: this.$t('import_performant_trot.col_cheval') },
                    { key: 'specialite',  label: this.$t('import_performant_trot.col_specialite') },
                    { key: 'hippodrome',  label: this.$t('import_performant_trot.col_hippodrome') },
                    { key: 'nom_prix',    label: this.$t('import_performant_trot.col_nom_course') },
                    { key: 'distance',    label: this.$t('import_performant_trot.col_distance') },
                    { key: 'driver',      label: this.$t('import_performant_trot.col_driver') },
                    { key: 'rang',        label: this.$t('import_performant_trot.col_rang') },
                    { key: 'gain',        label: this.$t('import_performant_trot.col_gain') },
                    { key: 'temps_str',   label: this.$t('import_performant_trot.col_temps') },
                ]
            },
        },
    }
</script>
